import type { Project } from "@/services/project.service";
import type { Columns, UiTask } from "./types";

// Swimlane cell droppable id = `${projectId}${CELL_SEP}${stageKey}`. Kept as a
// composite so a single DndContext can tell which project row + stage column a
// drop landed in, while the drag handlers only ever act on the stage part
// (project is locked — dragging never changes a task's project).
export const CELL_SEP = "::";

export const cellId = (projectId: string, stageKey: string) =>
	`${projectId}${CELL_SEP}${stageKey}`;

// Returns the stageKey of a swimlane cell droppable id, or null if `id` is not
// a cell id (e.g. it is a task id).
export function parseCellStageKey(id: string): string | null {
	const i = id.indexOf(CELL_SEP);
	return i >= 0 ? id.slice(i + CELL_SEP.length) : null;
}

export function isCellId(id: string): boolean {
	return id.includes(CELL_SEP);
}

export interface Swimlanes {
	// Project ids that have at least one task, sorted by project name A-Z. Sort
	// is by name (not first-seen) so moving a card never reorders the rows.
	projectIds: string[];
	// cellId -> tasks in that project row + stage column.
	cells: Record<string, UiTask[]>;
	// projectId -> total task count across all stage columns.
	totals: Record<string, number>;
}

// Reshape the already-filtered per-stage columns into swimlane cells. Task order
// within a cell is preserved from `filteredColumns`, so existing ordering/sort is
// untouched — this is a pure presentation reshape.
export function buildSwimlanes(
	filteredColumns: Columns,
	stageKeys: string[],
	projectsById: Map<string, Project>,
): Swimlanes {
	const seen = new Set<string>();
	const projectIds: string[] = [];
	const cells: Record<string, UiTask[]> = {};
	const totals: Record<string, number> = {};

	for (const stageKey of stageKeys) {
		for (const task of filteredColumns[stageKey] ?? []) {
			if (!seen.has(task.project_id)) {
				seen.add(task.project_id);
				projectIds.push(task.project_id);
			}
			const key = cellId(task.project_id, stageKey);
			(cells[key] ??= []).push(task);
			totals[task.project_id] = (totals[task.project_id] ?? 0) + 1;
		}
	}

	const nameOf = (id: string) => projectsById.get(id)?.name ?? "￿";
	projectIds.sort((a, b) =>
		nameOf(a).localeCompare(nameOf(b), undefined, { sensitivity: "base" }),
	);

	return { projectIds, cells, totals };
}
